import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import CalendlyModal from "@/components/CalendlyModal";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import SEOHead from "@/components/SEOHead";
import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Analytics } from "@/lib/analytics";
import { 
  MapPin, 
  Mail, 
  Send,
  Calendar,
  ArrowRight,
  CheckCircle
} from "lucide-react";
import { z } from "zod";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Le nom est requis").max(100, "Le nom ne doit pas dépasser 100 caractères"),
  email: z.string().trim().email("Adresse email invalide").max(255, "L'email ne doit pas dépasser 255 caractères"),
  phone: z.string().trim().max(20, "Le téléphone ne doit pas dépasser 20 caractères").optional().or(z.literal("")),
  message: z.string().trim().min(1, "Le message est requis").max(2000, "Le message ne doit pas dépasser 2000 caractères")
});

type ContactFormData = z.infer<typeof contactSchema>;

const useScrollReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-12");
          }
        });
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return ref;
};

const Contact = () => {
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  
  const { toast } = useToast();
  const heroRef = useScrollReveal();
  const formRef = useScrollReveal();
  const infoRef = useScrollReveal();

  useEffect(() => {
    // title set by SEOHead
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form data
    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          const field = err.path[0] as keyof ContactFormData;
          fieldErrors[field] = err.message;
          // Track form errors
          Analytics.formError("contact_form", field, err.message);
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("send_contact_email", {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          message: formData.message
        }
      });

      if (error) {
        Analytics.apiError("send_contact_email", 500, error.message);
        throw new Error(error.message || "Erreur lors de l'envoi");
      }

      setIsSubmitted(true);
      // Track contact form submission
      Analytics.contactFormSubmitted();
      toast({
        title: "Message envoyé !",
        description: "Nous vous répondrons dans les plus brefs délais.",
      });
    } catch (error: any) {
      console.error("Contact form error:", error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Contact Immovox | Démo et questions"
        description="Contactez Immovox pour tester l’IA téléphonique sur les appels de votre agence immobilière ou poser une question sur le fonctionnement du service."
        canonical="/contact"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div 
          ref={heroRef}
          className="max-w-4xl mx-auto text-center opacity-0 translate-y-12 transition-all duration-700 ease-out"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-[-.045em] text-foreground mb-6 leading-[1.03]">
            Parlons de votre
            <br />
            <span className="text-primary">projet</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Une question, une demande de démonstration ou simplement envie d'en savoir plus ? 
            Notre équipe est à votre écoute.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16">
          
          {/* Contact Form */}
          <div 
            ref={formRef}
            className="opacity-0 translate-y-12 transition-all duration-700 ease-out"
          >
            <div className="bg-card p-8 md:p-10 border border-border">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                Envoyez-nous un message
              </h2>
              
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-primary/10 rounded-md flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Message envoyé !
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: "", email: "", phone: "", message: "" });
                    }}
                  >
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Jean Dupont"
                        value={formData.name}
                        onChange={handleChange}
                        className={errors.name ? "border-destructive" : ""}
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive">{errors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email professionnel *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="jean@agence.fr"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone (optionnel)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={formData.phone}
                      onChange={handleChange}
                      className={errors.phone ? "border-destructive" : ""}
                    />
                    {errors.phone && (
                      <p className="text-sm text-destructive">{errors.phone}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Votre message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Décrivez votre besoin ou posez-nous vos questions..."
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className={errors.message ? "border-destructive" : ""}
                    />
                    {errors.message && (
                      <p className="text-sm text-destructive">{errors.message}</p>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      "Envoi en cours..."
                    ) : (
                      <>
                        Envoyer le message
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div 
            ref={infoRef}
            className="opacity-0 translate-y-12 transition-all duration-700 ease-out lg:pl-8"
          >
            <div className="space-y-8">
              {/* Quick Demo CTA */}
              <div className="bg-secondary/45 p-8 border-y border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 border border-border flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Préférez un échange direct ?</h3>
                    <p className="text-sm text-muted-foreground">30 minutes pour découvrir Immovox</p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  Réservez un créneau avec notre équipe pour une démonstration personnalisée de la solution.
                </p>
                <Button 
                  onClick={() => {
                    Analytics.calendlyModalOpened("contact_page");
                    setCalendlyOpen(true);
                  }}
                >
                  Planifier une démo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>

              {/* Contact Details */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-foreground">
                  Nos coordonnées
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Email</p>
                      <a 
                        href="mailto:contact@immovox.fr" 
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        contact@immovox.fr
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Localisation</p>
                      <p className="text-muted-foreground">Toulouse, France</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-secondary rounded-xl p-6">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Délai de réponse moyen :</span>
                  {" "}Nous nous engageons à vous répondre sous 24h ouvrées.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} />
      <ExitIntentPopup />
    </div>
  );
};

export default Contact;
